import {
  faChartLine,
  faBalanceScale,
  faUserPlus,
  faSync,
  faExclamationTriangle,
  faLeaf,
  faThumbtack,
  faRocket,
  faHandWave,
  faChartBar,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

/**
 * Standardized icon mapping for pod templates
 * This ensures consistent icon usage across Navigation and PodDirectory components
 */
const POD_ICON_MAP: Record<string, IconDefinition> = {
  // Specific pod name mappings
  "Daily Portfolio Review": faChartLine,
  "Monthly Auditing": faBalanceScale,
  "Ad Hoc Client Onboarding": faUserPlus,
  "Quarterly Rebalancing": faSync,
  "Risk Alert Response": faExclamationTriangle,
  "ESG Impact Assessment": faLeaf,

  // Icon name mappings (for API-based icon names)
  "fa-hand-wave": faHandWave,
  "fa-chart-bar": faChartBar,
  "fa-chart-line": faChartLine,
  "fa-balance-scale": faBalanceScale,
  "fa-user-plus": faUserPlus,
  "fa-sync": faSync,
  "fa-exclamation-triangle": faExclamationTriangle,
  "fa-leaf": faLeaf,
  "fa-rocket": faRocket,
  "fa-thumbtack": faThumbtack,
};

/**
 * Get the appropriate icon for a pod template
 * @param podName The name of the pod template
 * @param iconName Optional icon name from API (e.g., "fa-chart-line")
 * @returns The icon definition to use
 */
export function getPodIcon(podName: string, iconName?: string): IconDefinition {
  // First, try to get icon by the specific icon name from API
  if (iconName && POD_ICON_MAP[iconName]) {
    return POD_ICON_MAP[iconName];
  }

  // Then, try to get icon by pod name
  if (POD_ICON_MAP[podName]) {
    return POD_ICON_MAP[podName];
  }

  // Fallback to thumbtack icon
  return faRocket;
}

/** Pod template structure for icon resolution */
interface PodTemplate {
  icon?: string;
  icon_name?: string;
  template_icon?: string;
  name?: string;
  template_name?: string;
}

/**
 * Get pod icon with fallback handling for different data structures
 * This function handles the various ways pod data might be structured
 * @param pod The pod template object
 * @returns The icon definition to use
 */
export function getPodIconFromTemplate(pod: PodTemplate): IconDefinition {
  // Handle different possible icon field names
  const iconName = pod.icon || pod.icon_name || pod.template_icon;
  const podName = pod.name || pod.template_name;

  return getPodIcon(podName, iconName);
}
